import type {
  LocationQuery,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router
} from 'vue-router'
import { ACCOUNT_CONTEXT_QUERY_KEY, isAccountContextPath } from '@/shared/constants/accountContext'
import {
  RESELLER_CONTEXT_QUERY_KEY,
  isResellerContextPath
} from '@/shared/constants/resellerContext'
import { parseAccountQueryParam } from '@/shared/utils/accountContext'
import { parseResellerQueryParam } from '@/shared/utils/resellerContext'

export type ListScope = {
  resellerId?: string | number
  accountId?: string | number
}

function normalizePath(path: string): string {
  return path.split('?')[0]?.replace(/\/$/, '') || '/'
}

/** Read reseller/account scope from the current route query. */
export function readListScope(query: LocationQuery): ListScope {
  return {
    resellerId: parseResellerQueryParam(query[RESELLER_CONTEXT_QUERY_KEY]),
    accountId: parseAccountQueryParam(query[ACCOUNT_CONTEXT_QUERY_KEY])
  }
}

/** Convert scope to query key/value pairs (omits empty values). */
export function scopeToQuery(scope: ListScope): Record<string, string> {
  const query: Record<string, string> = {}
  if (scope.resellerId) {
    query[RESELLER_CONTEXT_QUERY_KEY] = String(scope.resellerId)
  }
  if (scope.accountId) {
    query[ACCOUNT_CONTEXT_QUERY_KEY] = String(scope.accountId)
  }
  return query
}

export function mergeScopeIntoQuery(baseQuery: LocationQuery, scope: ListScope): LocationQuery {
  const query = { ...baseQuery }

  if (scope.resellerId) {
    query[RESELLER_CONTEXT_QUERY_KEY] = String(scope.resellerId)
  } else {
    delete query[RESELLER_CONTEXT_QUERY_KEY]
  }

  if (scope.accountId) {
    query[ACCOUNT_CONTEXT_QUERY_KEY] = String(scope.accountId)
  } else {
    delete query[ACCOUNT_CONTEXT_QUERY_KEY]
  }

  return query
}

export type ListScopePatch = {
  resellerId?: string | number | null
  accountId?: string | number | null
}

/**
 * Apply hierarchy rules:
 * - Clearing reseller clears account
 * - Setting account can imply reseller from account metadata
 */
export function resolveListScope(
  current: ListScope,
  patch: ListScopePatch,
  accountResellerId?: string | number | null
): ListScope {
  let { resellerId, accountId } = current

  if ('resellerId' in patch) {
    resellerId = patch.resellerId ? patch.resellerId : undefined
    if (!resellerId) {
      accountId = undefined
    }
  }

  if ('accountId' in patch) {
    accountId = patch.accountId ? patch.accountId : undefined
    if (accountId && accountResellerId) {
      resellerId = accountResellerId
    }
  }

  return { resellerId, accountId }
}

function isScopeUnchanged(route: RouteLocationNormalizedLoaded, next: ListScope): boolean {
  return (
    String(route.query[RESELLER_CONTEXT_QUERY_KEY] ?? '') === String(next.resellerId ?? '') &&
    String(route.query[ACCOUNT_CONTEXT_QUERY_KEY] ?? '') === String(next.accountId ?? '')
  )
}

/** Update scope on the current route (single write path for dropdowns). */
export function replaceListScope(
  router: Router,
  route: RouteLocationNormalizedLoaded,
  patch: ListScopePatch,
  accountResellerId?: string | number | null
): void {
  const current = readListScope(route.query)
  const next = resolveListScope(current, patch, accountResellerId)
  if (isScopeUnchanged(route, next)) return

  router.replace({
    path: route.path,
    query: mergeScopeIntoQuery(route.query, next)
  })
}

/** Build list-route query; explicit ids override values from routeQuery. */
export function buildScopedListQuery(
  options?: {
    accountId?: string | number | null
    resellerId?: string | number | null
  },
  routeQuery?: LocationQuery
): Record<string, string> {
  const fromRoute = routeQuery ? readListScope(routeQuery) : {}
  const accountId =
    options && 'accountId' in options ? options.accountId || undefined : fromRoute.accountId
  const resellerId =
    options && 'resellerId' in options ? options.resellerId || undefined : fromRoute.resellerId
  return scopeToQuery({ accountId, resellerId })
}

/** Append scope to a navigation target when the destination supports it. */
export function buildRouteWithListScope(to: RouteLocationRaw, scope: ListScope): RouteLocationRaw {
  if (!scope.resellerId && !scope.accountId) return to

  if (typeof to === 'string') {
    const path = normalizePath(to)
    const query = scopeToQuery(scope)
    if (!scope.resellerId && !isAccountContextPath(path)) return to
    if (!scope.accountId && !isResellerContextPath(path)) return to
    if (!isAccountContextPath(path) && !isResellerContextPath(path)) return to
    return { path, query }
  }

  const path =
    'path' in to && to.path
      ? normalizePath(to.path)
      : 'name' in to && typeof to.name === 'string'
        ? undefined
        : undefined

  const existingQuery = typeof to === 'object' && to.query ? to.query : {}
  const mergedQuery = { ...existingQuery, ...scopeToQuery(scope) }

  if (path) {
    if (!isAccountContextPath(path) && !isResellerContextPath(path)) return to
    return { ...to, query: mergedQuery }
  }

  // Named route: merge scope into query when any scope keys apply
  return { ...to, query: mergedQuery }
}

/** Carry scope from the current route into a navigation target. */
export function buildRouteWithListScopeFromRoute(
  to: RouteLocationRaw,
  from: RouteLocationNormalizedLoaded
): RouteLocationRaw {
  return buildRouteWithListScope(to, readListScope(from.query))
}
